import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../firebase/firebaseConfig';
import { TOKEN_PACKS } from './tokenPacksConfig';
import './TokenPurchaseModal.css';

const TokenPurchaseModal = ({ isOpen, onClose, tokenBalance }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingPack, setProcessingPack] = useState(null);

  if (!isOpen) return null;

  const handlePurchase = async (packKey) => {
    if (!packKey || !TOKEN_PACKS[packKey]) {
      setError('Invalid token pack selected');
      return;
    }

    const pack = TOKEN_PACKS[packKey];
    setIsLoading(true);
    setProcessingPack(packKey);
    setError(null);

    try {
      const functions = getFunctions(app);
      const createTokenCheckoutSession = httpsCallable(functions, 'createTokenCheckoutSession');

      const result = await createTokenCheckoutSession({
        priceId: pack.priceId
      });

      if (result.data && result.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);

      // Extract more detailed error information
      let errorMessage = 'Failed to start purchase. Please try again.';

      if (error.code === 'functions/failed-precondition') {
        errorMessage = 'Payment system is not properly configured. Please contact support.';
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage = 'Please sign in to purchase tokens.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message || 'Invalid purchase request. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.details) {
        errorMessage = error.details;
      }

      setError(errorMessage);
      setIsLoading(false);
      setProcessingPack(null);
    }
  };

  return (
    <div className="token-purchase__backdrop" onClick={onClose}>
      <div
        className="token-purchase__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="token-purchase-title"
      >
        <div className="token-purchase__header">
          <h3 id="token-purchase-title" className="token-purchase__title">Tokens</h3>
          <button className="token-purchase__close-btn" onClick={onClose} aria-label="Close token purchase">
            ×
          </button>
        </div>

        <div className="token-purchase__balance">
          <span className="token-purchase__balance-label">Your balance</span>
          <span className="token-purchase__balance-value">{Number.isFinite(tokenBalance) ? tokenBalance : 0}</span>
        </div>

        {error && (
          <div className="token-purchase__error" style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: 'rgba(255, 71, 87, 0.1)',
            border: '1px solid rgba(255, 71, 87, 0.3)',
            borderRadius: '8px',
            color: '#ff4757',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div className="token-purchase__options">
          <div className="token-purchase__option-card">
            <div className="token-purchase__option-title">{TOKEN_PACKS.starter.label}</div>
            <div className="token-purchase__option-meta">
              <span className="token-purchase__tokens">{TOKEN_PACKS.starter.tokens} tokens</span>
              <span className="token-purchase__price">${TOKEN_PACKS.starter.amount.toFixed(2)}</span>
            </div>
            <button
              className="token-purchase__buy-btn"
              onClick={() => handlePurchase('starter')}
              disabled={isLoading}
              title={isLoading ? 'Processing...' : 'Buy tokens'}
            >
              {isLoading && processingPack === 'starter' ? 'Processing...' : 'Buy'}
            </button>
          </div>

          <div className="token-purchase__option-card token-purchase__option-card--popular">
            <div className="token-purchase__option-title">{TOKEN_PACKS.popular.label}</div>
            <div className="token-purchase__option-meta">
              <span className="token-purchase__tokens">{TOKEN_PACKS.popular.tokens} tokens</span>
              <span className="token-purchase__price">${TOKEN_PACKS.popular.amount.toFixed(2)}</span>
            </div>
            <button
              className="token-purchase__buy-btn"
              onClick={() => handlePurchase('popular')}
              disabled={isLoading}
              title={isLoading ? 'Processing...' : 'Buy tokens'}
            >
              {isLoading && processingPack === 'popular' ? 'Processing...' : 'Buy'}
            </button>
          </div>

          <div className="token-purchase__option-card">
            <div className="token-purchase__option-title">{TOKEN_PACKS.value.label}</div>
            <div className="token-purchase__option-meta">
              <span className="token-purchase__tokens">{TOKEN_PACKS.value.tokens} tokens</span>
              <span className="token-purchase__price">${TOKEN_PACKS.value.amount.toFixed(2)}</span>
            </div>
            <button
              className="token-purchase__buy-btn"
              onClick={() => handlePurchase('value')}
              disabled={isLoading}
              title={isLoading ? 'Processing...' : 'Buy tokens'}
            >
              {isLoading && processingPack === 'value' ? 'Processing...' : 'Buy'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenPurchaseModal;


